import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import Tasks from 'APP/Tasks';
import { SecretChatRequestAccessPopup } from 'APP/main/PopupManager/Popups/SecretChatRequestAccessPopup/SecretChatRequestAccessPopup';
import { ISecretChatAccessRequest } from 'APP/model/e2ee/e2eeModel.types';
import Entities from 'STORE';
import { SecretGroup } from 'STORE/Groups/SecretGroup/SecretGroup';
import type { User } from 'STORE/Users/User/User';

interface ISecretChatAccessRequestPresenter {
  group: SecretGroup | null;
  isShowSecretChatAccessRequestBanner: boolean;
  accessRequests: readonly ISecretChatAccessRequest[] | null;
  accessRequestsCount: number;
  user: User | null;
  groupId: string;
  onViewRequests(): void;
  setGroupId(id: string): void;
}

export const useSecretChatAccessRequestPresenter = (
  groupId: string
): ISecretChatAccessRequestPresenter => {
  const presenter = useLocalObservable<ISecretChatAccessRequestPresenter>(() => ({
    groupId: groupId,

    setGroupId(id: string): void {
      presenter.groupId = id;
    },

    get group(): SecretGroup | null {
      const group = Entities.GroupsStore.getGroupById(presenter.groupId) || null;

      if (group && group instanceof SecretGroup) {
        return group;
      }

      return null;
    },

    get accessRequests(): readonly ISecretChatAccessRequest[] | null {
      return presenter.group?.devicesRequestsAccessList || null;
    },

    get accessRequestsCount(): number {
      return presenter.group?.chatAccessRequestsCount || 0;
    },

    get user(): User | null {
      const userId = presenter.accessRequests ? presenter.accessRequests[0].userId : null;

      if (!userId) {
        return null;
      }

      return Entities.usersStore.getUserById(userId);
    },

    get isShowSecretChatAccessRequestBanner(): boolean {
      return Boolean(presenter.accessRequests?.length) && !!presenter.group?.withMeInAdmins;
    },

    onViewRequests(): void {
      Tasks.app.openPopup(SecretChatRequestAccessPopup, { params: { groupId: presenter.groupId } });
    },
  }));

  useEffect(() => {
    presenter.setGroupId(groupId);
  }, [groupId]);

  return presenter;
};
